import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ProgressSlideType, TopsyTurvyProgressSlide } from ".";
import { DayTransition, isDayTransitionSeen } from "./DayTransition";
import { useSessionContext } from "../../../../stores/session";

/**
 * Determines which day slide is currently active based on timer end times.
 * Each day has a relatedMedia entry with title "timer" whose label is an ISO date string.
 * A day is "ended" if its timer has passed.
 */
function getCurrentDayIndex(sortedSlides: ProgressSlideType[]): number {
    const now = Date.now();

    for (let i = 0; i < sortedSlides.length; i++) {
        const slide = sortedSlides[i];
        if (!slide) continue;
        const timerLabel = slide.relatedMedia?.find(
            (m) => m.title === "timer",
        )?.label;

        if (!timerLabel) {
            return i;
        }

        const endTime = new Date(timerLabel).getTime();
        if (now < endTime) {
            return i;
        }
    }

    return sortedSlides.length - 1;
}

export const MultidayTopsyTurvy = ({
    slides,
}: {
    slides: ProgressSlideType[];
}) => {
    const {
        state: { avatarId },
    } = useSessionContext();

    const sortedSlides = useMemo(
        () =>
            [...slides].sort((a, b) => {
                const aDay = parseInt(
                    a.relatedMedia?.find((m) => m.title === "day")?.label ??
                        "0",
                    10,
                );
                const bDay = parseInt(
                    b.relatedMedia?.find((m) => m.title === "day")?.label ??
                        "0",
                    10,
                );
                return aDay - bDay;
            }),
        [slides],
    );

    // Re-evaluate currentDayIndex when the current day's timer expires
    const [, forceUpdate] = useState(0);
    const currentDayIndex = getCurrentDayIndex(sortedSlides);

    useEffect(() => {
        const currentSlide = sortedSlides[currentDayIndex];
        const timerLabel = currentSlide?.relatedMedia?.find(
            (m) => m.title === "timer",
        )?.label;

        if (!timerLabel) return;

        const msUntilExpiry = new Date(timerLabel).getTime() - Date.now();
        if (msUntilExpiry <= 0) return;

        // Clamp to max safe setTimeout value (2^31 - 1 ms ≈ 24.8 days)
        // to avoid 32-bit overflow causing immediate firing
        const MAX_TIMEOUT = 2_147_483_647; // 2^31 - 1
        const timeout = setTimeout(
            () => {
                forceUpdate((n) => n + 1);
            },
            Math.min(msUntilExpiry, MAX_TIMEOUT),
        );

        return () => clearTimeout(timeout);
    }, [sortedSlides, currentDayIndex]);

    const [dismissedTransitions, setDismissedTransitions] = useState<
        Set<string>
    >(new Set());

    // Track whether the transition overlay is currently fading out
    // so we keep it mounted until the animation finishes
    const [fadingOutId, setFadingOutId] = useState<string | null>(null);

    const pendingTransitionIndex = useMemo(() => {
        for (let i = 0; i < currentDayIndex; i++) {
            const slide = sortedSlides[i];
            if (
                slide &&
                !isDayTransitionSeen(slide.id, avatarId) &&
                !dismissedTransitions.has(slide.id)
            ) {
                return i;
            }
        }
        return null;
    }, [sortedSlides, currentDayIndex, dismissedTransitions]);

    const handleTransitionContinue = useCallback(() => {
        if (pendingTransitionIndex === null) return;

        const slide = sortedSlides[pendingTransitionIndex];
        if (!slide) return;

        // Start the fade-out animation
        setFadingOutId(slide.id);

        // Must match day-transition-fade-out duration (0.4s) in topsy-turvy/index.scss
        setTimeout(() => {
            setFadingOutId(null);
            setDismissedTransitions((prev) => new Set([...prev, slide.id]));
        }, 500);
    }, [pendingTransitionIndex, sortedSlides]);

    // Always render the current (or next) day's tracker as the base
    const currentSlide = sortedSlides[currentDayIndex];
    if (!currentSlide) return null;

    // Show the transition overlay on top if needed
    const showTransition =
        pendingTransitionIndex !== null && fadingOutId === null;
    const showFadingTransition = fadingOutId !== null;

    const transitionSlide =
        pendingTransitionIndex !== null
            ? (sortedSlides[pendingTransitionIndex] ?? null)
            : null;

    const fadingSlide = showFadingTransition
        ? sortedSlides.find((s) => s.id === fadingOutId)
        : null;

    return (
        <TopsyTurvyProgressSlide slide={currentSlide}>
            {showTransition && transitionSlide && (
                <DayTransition
                    completedDay={transitionSlide}
                    onContinue={handleTransitionContinue}
                    isFadingOut={false}
                />
            )}
            {showFadingTransition && fadingSlide && (
                <DayTransition
                    completedDay={fadingSlide}
                    onContinue={handleTransitionContinue}
                    isFadingOut={true}
                />
            )}
        </TopsyTurvyProgressSlide>
    );
};
